/**
 * Que usado del stock se manda a reacondicionar.
 *
 * Se elige la UNIDAD, no se tipea el IMEI: el IMEI es unico en `productos`,
 * asi que elegir el usado identifica el equipo sin ambiguedad. Se busca por
 * modelo o por IMEI entre los usados del local con stock; los que ya estan en
 * el taller se ven pero no se pueden elegir, con su numero de orden al lado.
 */
import { Fragment, useMemo, useState, type ReactNode } from "react";
import { Smartphone, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Campo } from "@/components/ui/campo";
import { Card } from "@/components/ui/card";
import { Codigo } from "@/components/ui/codigo";
import { IconButton } from "@/components/ui/icon-button";
import { InputBusqueda } from "@/components/ui/input-busqueda";
import { Label } from "@/components/ui/label";
import { Importe } from "@/components/money/importe";
import { aNumero, type Producto, type UsadoEnTaller } from "@/lib/tipos";

import { usadosElegibles } from "./logica";

export interface ElegirUsadoProps {
  usados: ReadonlyArray<Producto>;
  enTaller: ReadonlyMap<number, UsadoEnTaller>;
  productoId: number | null;
  onElegir: (p: Producto | null) => void;
  problema?: string;
}

/** IMEI, bateria y lo que se pago por el: lo que distingue un usado de otro igual. */
function detalleDe(p: Producto): ReactNode[] {
  const partes: ReactNode[] = [];
  if (p.imei) {
    partes.push(
      <>
        IMEI <Codigo>{p.imei}</Codigo>
      </>,
    );
  }
  // El color salio del nombre y pasó a ser un campo: si no se dice acá, dos
  // equipos del mismo modelo y la misma memoria se ven idénticos.
  if (p.color) partes.push(p.color);
  if (p.bateria_pct !== null) partes.push(`bat. ${p.bateria_pct}%`);
  if (aNumero(p.costo) > 0) {
    partes.push(
      <>
        toma <Importe monto={aNumero(p.costo)} moneda={p.costo_moneda} tamano="sm" />
      </>,
    );
  }
  return partes;
}

export function ElegirUsado({
  usados,
  enTaller,
  productoId,
  onElegir,
  problema,
}: ElegirUsadoProps): JSX.Element {
  const [texto, setTexto] = useState("");

  const elegido = useMemo(
    () => usados.find((p) => p.id === productoId) ?? null,
    [usados, productoId],
  );
  const candidatos = useMemo(() => usadosElegibles(usados, texto), [usados, texto]);

  if (elegido) {
    return (
      <div className="space-y-1.5">
        <Label>Equipo del stock</Label>
        <Card className="flex items-center justify-between gap-2 py-1 pl-3 pr-1">
          <span className="flex min-w-0 items-center gap-2 text-sm">
            <Smartphone className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="truncate">{elegido.nombre}</span>
            {elegido.imei ? (
              <Codigo className="shrink-0 text-xs text-muted-foreground">{elegido.imei}</Codigo>
            ) : null}
          </span>
          <IconButton
            icono={X}
            aria-label="Elegir otro equipo"
            tamano="sm"
            onClick={() => onElegir(null)}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Campo label="Qué usado del stock se manda" requerido error={problema}>
        <InputBusqueda
          id="ot-usado"
          etiqueta="Qué usado del stock se manda"
          valor={texto}
          onValorChange={setTexto}
          placeholder="Buscá por modelo o IMEI"
        />
      </Campo>

      <Card padding="none" className="overflow-hidden">
        <ul className="divide-y divide-border">
          {candidatos.length === 0 ? (
            <li className="px-3 py-2 text-xs text-muted-foreground">
              No hay ningún usado en stock que coincida.
            </li>
          ) : (
            candidatos.map((p) => {
              const t = enTaller.get(p.id);
              const detalle = detalleDe(p);
              return (
                <li key={p.id}>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={Boolean(t)}
                    onClick={() => onElegir(p)}
                    className="h-auto w-full justify-between rounded-none px-3 py-2 text-left font-normal"
                  >
                    <span className="min-w-0">
                      <span className="block truncate">{p.nombre}</span>
                      {detalle.length > 0 ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {detalle.map((parte, i) => (
                            <Fragment key={i}>
                              {i > 0 ? " · " : null}
                              {parte}
                            </Fragment>
                          ))}
                        </span>
                      ) : null}
                    </span>
                    {t ? (
                      <Badge variant="warning" className="shrink-0">
                        En taller · {t.numero}
                      </Badge>
                    ) : null}
                  </Button>
                </li>
              );
            })
          )}
        </ul>
      </Card>
    </div>
  );
}
